'use client';

import { IResolvedDestinationDetailsPlan } from '@/src/utils/pages/destination-details.mapper';
import { IFirestoreZone } from '@/src/modules/firestore/zones/zones.interface';
import { IFirestoreOffer } from '@/src/modules/firestore/offers/offers.interface';
import { IFirestorePromo } from '@/src/modules/firestore/promos/promos.interface';
import { DestinationDetailsName } from '@/src/components/ui/DestinationDetails/DestinationDetailsName';
import { DestinationDetailsTitle } from '@/src/components/ui/DestinationDetails/DestinationDetailsTitle';
import { DestinationDetailsTechBanner } from '@/src/components/ui/DestinationDetails/DestinationDetailsTechBanner';
import { DestinationDetailsOffers } from '@/src/components/ui/DestinationDetails/DestinationDetailsOffers/DestinationDetailsOffers';
import { DestinationDetailsListCountries } from '@/src/components/ui/DestinationDetails/DestinationDetailsListCountries';
import { DestinationDetailsImage } from '@/src/components/ui/DestinationDetails/DestinationDetailsImage';
import { IResolvedCountry } from '@/src/utils/country';
import { DestinationDetailsReviews } from '@/src/components/ui/DestinationDetails/DestinationDetailsReviews';
import { ILocaleType } from '@/src/types';
import { IResolvedErrors } from '@/src/utils/collections/errors.mapper';
import { IResolvedESimCompatible } from '@/src/utils/collections/eSim-compatible.mapper';
import { DestinationDetailsESimCompatible } from '@/src/components/ui/DestinationDetails/DestinationDetailsESimCompatible';

type IDestinationDetailsProps = {
  resolvedDestinationDetails: IResolvedDestinationDetailsPlan;
  zone: IFirestoreZone;
  offers: IFirestoreOffer[];
  promos: Record<string, IFirestorePromo>;
  countries?: IResolvedCountry[];
  locale: ILocaleType;
  errorTexts?: IResolvedErrors | null;
  eSimCompatible?: IResolvedESimCompatible;
};

export const DestinationDetails = ({
  resolvedDestinationDetails,
  zone,
  offers,
  countries,
  promos,
  locale,
  errorTexts,
  eSimCompatible,
}: IDestinationDetailsProps) => {
  return (
    <div
      className={`mx-auto mb-24 flex max-w-1200 flex-col gap-8 px-4 md:flex-row`}
    >
      <DestinationDetailsImage
        mobile={resolvedDestinationDetails.image.mobile}
        desktop={resolvedDestinationDetails.image.desktop}
      />
      <div
        className={
          'flex max-w-[700px] flex-1 flex-col items-start justify-between gap-4 space-y-4 lg:space-y-0'
        }
      >
        <div className={'space-y-1 lg:mt-0'}>
          <DestinationDetailsName
            name={zone.name}
            flagName={zone.flagName}
            esimFor={resolvedDestinationDetails.esimFor}
          />
          <DestinationDetailsReviews
            reviews={resolvedDestinationDetails.reviews}
          />
          <DestinationDetailsTitle title={resolvedDestinationDetails.title} />
          <div className={'items-center gap-2 pt-2 md:flex [&_div]:m-2'}>
            <DestinationDetailsESimCompatible eSimCompatible={eSimCompatible} />
            <DestinationDetailsTechBanner techBanner={zone.techBanner} />
            <DestinationDetailsListCountries
              countries={countries}
              titleDialog={resolvedDestinationDetails.countryForDestination}
              listCountryText={resolvedDestinationDetails.listCountry}
            />
          </div>
        </div>

        <DestinationDetailsOffers
          zone={zone}
          offers={offers.sort((a, b) => b.basePriceEuros - a.basePriceEuros)}
          promos={promos}
          locale={locale}
          destination={resolvedDestinationDetails}
          errorTexts={errorTexts}
        />
      </div>
    </div>
  );
};
